import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Navbar, MobileNav } from "@/components/Navbar";
import { api, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CompBadge } from "@/components/OpportunityCard";
import { ShareButton } from "@/components/ShareButton";
import { AddToCalendar } from "@/components/AddToCalendar";
import { PageMeta } from "@/components/PageMeta";
import { BusinessVerifiedSocials } from "@/components/BusinessVerifiedSocials";
import { Button } from "@/components/ui/button";
import { opportunityShareUrl } from "@/lib/share";
import { MapPin, Loader2, Send, Sparkles } from "lucide-react";

export default function PublicOpportunity() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [o, setO] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/public/opportunity/${id}`).then((r) => setO(r.data)).catch(() => setO(null)).finally(() => setLoading(false));
  }, [id]);

  const apply = () => {
    const target = `/opportunities/${id}`;
    if (user) { navigate(target); return; }
    const params = new URLSearchParams({ mode: "register", role: "creator", src: "shared_opportunity", ref_id: id, redirect: target });
    navigate(`/auth?${params.toString()}`);
  };

  if (loading) return <div className="flex h-screen items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-emerald-600" /></div>;
  if (!o) return (
    <div className="min-h-screen bg-[#FAF9F6]"><Navbar />
      <div className="mx-auto max-w-lg px-4 py-24 text-center">
        <p className="font-display text-xl font-bold text-zinc-800">This opportunity is no longer available</p>
        <Button data-testid="public-opp-browse" onClick={() => navigate("/discover")} className="mt-6 rounded-full bg-emerald-600 hover:bg-emerald-700">Explore Nearfluence</Button>
      </div>
    </div>
  );

  const dl = o.deliverables_list?.length ? o.deliverables_list.map((x) => `${x.quantity} ${x.type}`).join(" + ") : o.deliverables;
  const Row = ({ label, value }) => value ? <div className="flex justify-between gap-4 border-b border-zinc-100 py-2 text-sm"><span className="text-zinc-500">{label}</span><span className="text-right font-medium text-zinc-900">{value}</span></div> : null;

  return (
    <div className="min-h-screen bg-[#FAF9F6] nf-pb-action-10">
      <PageMeta title={`${o.title} · ${o.business_name} · Nearfluence`} description={`${o.business_name} • ${o.location}. ${(o.description || "").slice(0, 140)}`} />
      <Navbar />
      <div className="mx-auto max-w-3xl px-4 py-6 sm:px-6 lg:px-8">
        <div className="mb-4 flex items-center justify-between">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700"><Sparkles className="h-3.5 w-3.5" /> Opportunity on Nearfluence</span>
          <ShareButton testid="public-opp-share" url={opportunityShareUrl(id)} title={o.title}
            text={`${o.title} — ${o.business_name}, ${o.location}. Apply on Nearfluence.`} recordKind="opportunity" recordId={id} label="Share" />
        </div>

        <div className="overflow-hidden rounded-3xl border border-zinc-200 bg-white">
          {o.cover_image && <div className="h-48 w-full overflow-hidden bg-zinc-100"><img src={fileUrl(o.cover_image)} alt="" className="h-full w-full object-cover" /></div>}
          <div className="p-6">
            <div className="flex items-center gap-3">
              <div className="h-14 w-14 overflow-hidden rounded-xl bg-zinc-100">
                {o.business_logo ? <img src={fileUrl(o.business_logo)} alt="" className="h-full w-full object-cover" /> : <div className="flex h-full w-full items-center justify-center font-display font-bold text-zinc-400">{o.business_name?.[0]}</div>}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-zinc-500">{o.business_name}</p>
                <p className="flex items-center gap-1 text-xs text-zinc-400"><MapPin className="h-3 w-3" /> {o.location}</p>
              </div>
              <CompBadge type={o.compensation_type} />
            </div>

            {o.business_verified_socials?.length > 0 && (
              <BusinessVerifiedSocials socials={o.business_verified_socials} testid="public-opp-verified-socials" className="mt-3" showPosts />
            )}

            <h1 data-testid="public-opp-title" className="mt-4 font-display text-2xl font-bold text-zinc-900">{o.title}</h1>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {o.category && <span className="rounded-full bg-zinc-100 px-2.5 py-0.5 text-xs text-zinc-600">{o.category}</span>}
              {o.niches?.map((n) => <span key={n} className="rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs text-emerald-700">{n}</span>)}
              {o.platforms?.map((p) => <span key={p} className="rounded-full bg-zinc-100 px-2.5 py-0.5 text-xs capitalize text-zinc-600">{p}</span>)}
            </div>

            {o.description && <p className="mt-4 whitespace-pre-line text-zinc-600">{o.description}</p>}

            {o.opp_type === "event" && (
              <div data-testid="public-event-block" className="mt-4 rounded-2xl border border-fuchsia-200 bg-fuchsia-50/60 p-4 text-sm text-zinc-700">
                <span className="inline-flex items-center gap-1 rounded-full bg-fuchsia-600 px-2.5 py-0.5 text-xs font-bold text-white">EVENT</span>
                <div className="mt-2 space-y-1">
                  {o.venue_name && <p>📍 {o.venue_name}</p>}
                  {o.event_date && <p>📅 {o.event_date}{o.start_time ? ` · ${o.start_time}${o.end_time ? `–${o.end_time}` : ""}` : ""}</p>}
                  {o.what_creators_receive && <p>🎁 {o.what_creators_receive}</p>}
                  {o.complimentary && <p>🍽️ {o.complimentary}</p>}
                  {o.content_expectations && <p>🎬 {o.content_expectations}</p>}
                </div>
                {o.event_date && o.event_date >= new Date().toISOString().slice(0, 10) && (
                  <div className="mt-3"><AddToCalendar opportunity={o} testid="public-opp-add-calendar" /></div>
                )}
              </div>
            )}

            <div className="mt-5">
              <Row label="Deliverables" value={dl} />
              <Row label="Payment" value={o.compensation_type !== "gifted" && o.payment_amount != null ? `£${o.payment_amount}` : null} />
              <Row label="Gift / reward" value={o.gift_description} />
              <Row label="Creators required" value={String(o.creators_needed)} />
              <Row label="Min followers" value={o.min_followers ? o.min_followers.toLocaleString() : null} />
              <Row label="Application deadline" value={o.deadline} />
              <Row label="Additional requirements" value={o.additional_requirements} />
            </div>

            <div className="mt-6 rounded-2xl bg-zinc-900 p-5 text-center text-white">
              <p className="font-display text-lg font-bold">Want to work with {o.business_name}?</p>
              <p className="mt-1 text-sm text-zinc-300">Apply in seconds — Nearfluence sends your profile for you.</p>
              <Button data-testid="public-opp-apply" onClick={apply} className="mt-4 h-12 w-full rounded-full bg-emerald-500 text-base hover:bg-emerald-600 sm:w-auto sm:px-10">
                <Send className="mr-2 h-5 w-5" /> Apply on Nearfluence
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile sticky CTA */}
      <div className={`${user ? "nf-actionbar-abovenav" : "nf-actionbar-bottom"} border-t border-zinc-200 bg-white p-3 md:hidden`}>
        <Button data-testid="public-opp-apply-mobile" onClick={apply} className="h-12 w-full rounded-full bg-emerald-600 text-base hover:bg-emerald-700">
          <Send className="mr-2 h-5 w-5" /> Apply on Nearfluence
        </Button>
      </div>
      {user && <MobileNav />}
    </div>
  );
}
